import { Box, Button, Stack, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import {
  Control,
  FormProvider,
  SubmitHandler,
  useController,
  useFieldArray,
  useForm,
  UseFormClearErrors,
  UseFormSetError,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DEFAULT_CHARGING_CALENDAR_ENERGY_KWH, MAX_CHARGING_CALENDAR_ENERGY_KWH, MIN_CHARGING_CALENDAR_ENERGY_KWH } from 'src/Libs/flow/constants'
import { getDisabledSunModeDays, transformAbbreviatedToWeekdays, transformWeekdaysToAbbreviated } from 'src/Libs/flow/advanced-settings/utils'
import { ChargingSchedule, WeekDays } from 'src/Libs/flow/installation/forms'
import { getTimeAsISO } from 'src/Libs/flow/installation/utils'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { NumberField, TimePicker } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { Day } from '@e3dc-react/shell/Libs/date/types'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

export interface ChargingScheduleEditorDialogProps {
  initialSchedule: ChargingSchedule
  saveSchedule: (newShedule: ChargingSchedule) => void
  includeTime: boolean
  isLoading: boolean
  onClose?: () => void
}

/** Dialog to change the weekly charging schedule of an evse. */
export const ChargingScheduleEditorDialog: React.FC<ChargingScheduleEditorDialogProps> = ({
  initialSchedule,
  includeTime,
  saveSchedule,
  onClose,
  isLoading,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const methods = useForm({
    values: { schedule: initialSchedule },
    mode: 'onChange',
  })

  const { control, handleSubmit, reset, setError, clearErrors, formState } = methods
  const { isDirty } = formState
  const canSubmit = Object.keys(formState.errors).length === 0

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'schedule',
  })

  const schedule = useWatch({ control, name: 'schedule' })
  const { disabledDays, areAllDaysSelected } = getDisabledSunModeDays(schedule)

  const closeDialog = (): void => {
    onClose?.()
  }

  const addSchedule = (): void => {
    append({
      weekDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      departureTime: includeTime ? getTimeAsISO('06:00') : '',
      energyWanted: DEFAULT_CHARGING_CALENDAR_ENERGY_KWH,
    })
  }

  const onSubmit: SubmitHandler<{
    schedule: ChargingSchedule
  }> = data => {
    saveSchedule(data.schedule)
    closeDialog()
  }

  const dialogActions: DialogActions = {
    primary: {
      name: t('common.save'),
      onClick: handleSubmit(onSubmit),
      disabled: !userCanEdit || !canSubmit || isLoading,
      isLoading: isLoading,
    },
    secondary: {
      name: t('common.cancel'),
      onClick: closeDialog,
    },
  }
  const additionalActionButton = (
    <Button variant="contained" sx={{ minWidth: '100%' }} onClick={() => reset()} disabled={!userCanEdit || isLoading || !isDirty}>
      {t('common.reset')}
    </Button>
  )

  return (
    <Dialog
      title={t('user-settings.charging-priority-page.schedule.title')}
      additionalActionsLeft={additionalActionButton}
      maxWidth="md"
      actions={dialogActions}
    >
      <FormProvider {...methods}>
        {fields.map((field, index) => (
          <ChargingScheduleEntry
            key={field.id}
            index={index}
            control={control}
            disabledDays={disabledDays[index]}
            includeTime={includeTime}
            onRemove={() => remove(index)}
            setError={setError}
            clearErrors={clearErrors}
            disableDeleteButton={fields.length <= 1}
          />
        ))}
        <Box>
          <Button variant="outlined" disabled={areAllDaysSelected || !userCanEdit} onClick={addSchedule}>
            {`+ ${t('common.add')}`}
          </Button>
          {areAllDaysSelected && (
            <Typography variant="caption" sx={{ ml: 3 }}>
              {t('user-settings.charging-priority-page.schedule.all-days-used')}
            </Typography>
          )}
        </Box>
      </FormProvider>
    </Dialog>
  )
}

interface ChargingScheduleEntryProps {
  includeTime: boolean
  index: number
  control: Control<{ schedule: ChargingSchedule }, unknown>
  onRemove: () => void
  disabledDays: WeekDays | undefined
  setError: UseFormSetError<{ schedule: ChargingSchedule }>
  clearErrors: UseFormClearErrors<{ schedule: ChargingSchedule }>
  disableDeleteButton: boolean
}

/** Dialog to change the weekly charging schedule of an evse. */
const ChargingScheduleEntry: React.FC<ChargingScheduleEntryProps> = ({
  includeTime,
  index,
  control,
  onRemove,
  disabledDays,
  setError,
  clearErrors,
  disableDeleteButton,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const { field } = useController({
    control,
    name: `schedule.${index}.weekDays`,
  })

  const { errors } = useFormState({ control, name: `schedule.${index}` })
  const isError = !!Object.keys(errors.schedule?.[index] ?? {}).length

  const weekdays = useWatch({ control, name: `schedule.${index}.weekDays` })

  const noWeekdaySelected = Object.values(weekdays).every(day => day === false)

  useEffect(() => {
    if (noWeekdaySelected) {
      setError(`schedule.${index}.weekDays`, { type: 'charging-schedule-error' })
    } else {
      clearErrors(`schedule.${index}.weekDays`)
    }
  }, [noWeekdaySelected, setError, clearErrors, index])

  const handleDaySelect = (dayName: Day, selected: boolean): void => {
    const updatedDays = transformAbbreviatedToWeekdays({
      ...transformWeekdaysToAbbreviated(field.value),
      [dayName]: selected,
    })
    field.onChange({
      ...field.value,
      ...updatedDays,
    })
  }

  const getDisabledDaysArray = (): Day[] => {
    if (!disabledDays) return []

    const abbreviatedDays = transformWeekdaysToAbbreviated(disabledDays)

    return Object.entries(abbreviatedDays)
      .filter(([_, isDisabled]) => isDisabled)
      .map(([dayName]) => dayName as Day)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        bgcolor: 'shades.shade04',
        border: '1px solid',
        borderColor: 'shades.shade03',
        borderRadius: 1,
        px: 4,
        py: 2,
        mb: 2,
      }}
    >
      <Stack mb={4} gap={2}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant={'body1'} sx={{ color: isError ? 'palette.error.main' : undefined }}>
            {t('installation.evseEditor.chargingTargetRule')} {index + 1}
          </Typography>

          <Button variant="text" onClick={onRemove} disabled={!userCanEdit || disableDeleteButton}>
            {t('common.delete')}
          </Button>
        </Stack>

        <Box>
          <WeekDaySelector
            days={transformWeekdaysToAbbreviated(weekdays)}
            onSelectDay={handleDaySelect}
            disabledDays={getDisabledDaysArray()}
            disabled={!userCanEdit}
          />
          {noWeekdaySelected && <Alert severity="error" text={t('installation.tariff.missingDay')} sx={{ p: 1, mt: 1 }} />}
        </Box>

        {includeTime && (
          <TimePicker
            name={`schedule.${index}.departureTime`}
            label={t('common.from')}
            fullWidth
            disabled={!userCanEdit}
            onTransform={value => (value ? value.toISOString() : null)}
          />
        )}

        <NumberField
          name={`schedule.${index}.energyWanted`}
          label={t('installation.evseEditor.energy')}
          suffix={UnitTypes.kiloWattHour}
          disabled={!userCanEdit}
          precision={1}
          min={MIN_CHARGING_CALENDAR_ENERGY_KWH}
          max={MAX_CHARGING_CALENDAR_ENERGY_KWH}
          sx={{
            '& input': {
              textAlign: 'right',
            },
          }}
        />
      </Stack>
    </Box>
  )
}
